import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { Page, Reveal, MaskedLines } from '../components/motion';
import { CTASection } from '../components/CTASection';
import { SERVICES } from '../data/content';
import { useSEO } from '../hooks/useSEO';

export default function Services() {
  useSEO('Services', 'Building construction, design & build, and civil engineering services — roads, drainages and dams — delivered across Nigeria by Ehimo Building & Civil Engineering.');
  return (
    <Page>
      <section className="pt-40 pb-20 lg:pt-52 lg:pb-28 border-b border-line relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal y={14} className="mb-6">
            <p className="text-xs uppercase tracking-[0.3em] text-accent">What we do</p>
          </Reveal>
          <MaskedLines
            lines={['Services built on', 'engineering discipline.']}
            delay={0.1}
            lineClassName="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.95]"
          />
          <Reveal delay={0.4} className="mt-8 max-w-xl">
            <p className="text-mute leading-relaxed">
              We use the most modern construction technology in the world — across building construction, design &
              build, and civil engineering.
            </p>
          </Reveal>
        </div>
        <span className="absolute -bottom-10 right-0 font-display text-[22vw] leading-none text-outline select-none pointer-events-none" aria-hidden="true">
          01—03
        </span>
      </section>

      {SERVICES.map((s, i) => (
        <section
          key={s.id}
          data-testid={`service-section-${s.id}`}
          className={`text-ink ${i % 2 === 1 ? 'bg-white' : 'bg-paper'}`}
        >
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
              <Reveal className="flex items-center gap-4 mb-6" y={16}>
                <span className="font-display text-6xl lg:text-7xl font-semibold text-outline-ink">{s.number}</span>
                <span className="h-px flex-1 bg-paperline" aria-hidden="true" />
              </Reveal>
              <MaskedLines
                inView
                lines={[s.title]}
                lineClassName="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-tight"
              />
              <Reveal delay={0.15} className="mt-6 max-w-xl">
                <p className="text-inkmute leading-relaxed">{s.detail}</p>
              </Reveal>
              <Reveal delay={0.25} className="mt-8 space-y-3">
                {s.covers.map((c) => (
                  <p key={c} className="flex items-center gap-3 text-sm text-ink/80">
                    <span className="w-6 h-6 bg-accent flex items-center justify-center shrink-0">
                      <Check size={13} className="text-ink" />
                    </span>
                    {c}
                  </p>
                ))}
              </Reveal>
              <Reveal delay={0.35} className="mt-10">
                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.id}`}
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink font-medium border-b-2 border-accent pb-1 hover:bg-accent transition-colors"
                >
                  Discuss your project <ArrowUpRight size={16} />
                </Link>
              </Reveal>
            </div>
            <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
              <Reveal className="group border border-paperline bg-white p-3">
                <div className="overflow-hidden">
                  <img
                    src={s.image}
                    alt={`${s.title} — Ehimo Building & Civil Engineering`}
                    loading="lazy"
                    className="img-treated w-full aspect-[4/3] object-cover group-hover:scale-105"
                  />
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-inkmute">
                  {s.number} — {s.title}
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <CTASection title={['Ready when', 'you are.']} />
    </Page>
  );
}
